import { BaseHighchart } from '@/common/dashboard/charts/BaseHighchart';
import { useTheme } from '@mui/material/styles';

export const CumulativeSalesChart = ({
  categories,
  priorCategories,
  series,
  tierValues,
  tierLabelOffset = 0,
  startDate,
  markerDate,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  const labelStyle = { color: theme.palette.text.secondary };

  if (!categories?.length || !series?.length) return null;

  const values = series[0]?.values || [];
  const priorValues = series[1]?.values;
  const priorLabel = series[1]?.label || 'Prior Period';
  const hasPrior = Array.isArray(priorValues) && priorValues.length > 0;
  const tiers = (Array.isArray(tierValues) ? tierValues : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  const thresholdColors = [theme.palette.error.main, theme.palette.warning.main, theme.palette.success.main];
  const colorForTier = (tierNumber) => {
    if (tierNumber < 0) {
      return thresholdColors[0];
    }
    return thresholdColors[Math.min(tierNumber, thresholdColors.length - 1)];
  };

  const zones = [];
  if (tiers.length) {
    for (let i = 0; i < tiers.length; i++) {
      const tierNumber = i - 1 + tierLabelOffset;
      zones.push({ value: tiers[i], color: colorForTier(tierNumber) });
    }
    zones.push({ color: colorForTier(tiers.length - 1 + tierLabelOffset) });
  }

  const plotLines = tiers.map((tierVal, i) => ({
    tierNumber: i + tierLabelOffset,
    value: tierVal,
    color: colorForTier(i + tierLabelOffset),
    width: 2,
    dashStyle: 'Dash',
    label: {
      text: `Tier ${i + tierLabelOffset}: $${Number(tierVal).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      align: 'left',
      style: { color: colorForTier(i + tierLabelOffset), fontSize: '11px' },
    },
    zIndex: 4,
  }));

  const priorPoints =
    hasPrior ?
      categories.map((_, i) => ({
        x: i,
        y: Number(priorValues[i]) || 0,
      }))
    : [];

  // Categories are day labels without a year, so the window start date anchors today's offset. A period
  // that has already closed has no marker and no projection.
  let markerIndex = -1;
  const startDateStr = String(startDate ?? '');
  const markerDateStr = String(markerDate ?? '');
  if (startDateStr.length === 8 && markerDateStr.length === 8 && categories.length) {
    const asDate = (str) => new Date(Number(str.slice(0, 4)), Number(str.slice(4, 6)) - 1, Number(str.slice(6, 8)));
    const diffDays = Math.round((asDate(markerDateStr) - asDate(startDateStr)) / (24 * 60 * 60 * 1000));
    if (diffDays >= 0 && diffDays < categories.length) {
      markerIndex = diffDays;
    }
  }

  // Sales only exist up to the selected date, so the solid line stops there and the remainder of the
  // period is projected.
  const lastActualIndex = markerIndex >= 0 ? markerIndex : categories.length - 1;
  const actuals = categories.slice(0, lastActualIndex + 1).map((_, i) => Number(values[i]) || 0);

  const points = actuals.map((y, i) => ({ x: i, y }));

  // Least-squares slope of the sales to date, anchored at the selected date so the projection carries
  // the line forward instead of jumping off it. Matches the run-out the projected rebate card reports.
  const projPoints = [];
  if (markerIndex >= 1 && markerIndex < categories.length - 1) {
    const n = actuals.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    for (let x = 0; x < n; x++) {
      sumX += x;
      sumY += actuals[x];
      sumXY += x * actuals[x];
      sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    const anchor = actuals[n - 1];
    for (let i = markerIndex; i < categories.length; i++) {
      projPoints.push({ x: i, y: Math.max(anchor + slope * (i - markerIndex), 0) });
    }
  }
  const hasProjection = projPoints.length > 0;

  const maxValue = Math.max(
    ...actuals,
    ...projPoints.map((p) => p.y),
    ...priorPoints.map((p) => p.y),
    tiers.length ? tiers[tiers.length - 1] : 0,
  );

  // Find month boundary indices for vertical separator lines
  const xPlotLines = [];
  if (markerIndex >= 0) {
    xPlotLines.push({
      value: markerIndex,
      color: theme.palette.info.main,
      width: 2,
      dashStyle: 'Solid',
      zIndex: 5,
      label: {
        text: 'Today',
        align: 'center',
        rotation: 0,
        y: -5,
        style: { color: theme.palette.info.main, fontSize: '11px', fontWeight: 'bold' },
      },
    });
  }
  for (let i = 1; i < categories.length; i++) {
    const prevMonth = categories[i - 1]?.split('/')[0];
    const currMonth = categories[i]?.split('/')[0];
    if (prevMonth && currMonth && prevMonth !== currMonth) {
      xPlotLines.push({
        value: i - 0.5,
        color: dividerColor,
        width: 1,
        dashStyle: 'ShortDash',
        zIndex: 3,
      });
    }
  }

  const options = {
    chart: {
      spacing: [24, 8, 8, 8],
      animation: { duration: 300 },
    },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: hasPrior || hasProjection, align: 'right', verticalAlign: 'top', itemStyle: labelStyle },
    xAxis: {
      categories,
      tickLength: 0,
      lineWidth: 1,
      lineColor: dividerColor,
      gridLineWidth: 0,
      plotLines: xPlotLines,
      labels: {
        step: Math.ceil(categories.length / 12),
        style: labelStyle,
      },
      title: {
        text: 'Day',
        style: labelStyle,
        margin: 10,
      },
    },
    yAxis: {
      min: 0,
      max: Math.ceil(maxValue * 1.15),
      title: {
        text: 'Cumulative Sales ($)',
        style: labelStyle,
      },
      lineWidth: 0,
      gridLineWidth: 1,
      gridLineColor: dividerColor,
      gridLineDashStyle: 'ShortDot',
      plotLines,
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const points = this.points || (this.point ? [this.point] : [this]);
        const pointX = (p) => p?.point?.x ?? p?.x;
        const pointFor = (id) => points.find((p) => p.series?.options?.id === id);
        const actualPoint = pointFor('cumulative-sales');
        const projPoint = pointFor('projected-cumulative-sales');
        // The two lines only overlap on the selected date, and past it the solid line either drops out of
        // the shared tooltip or snaps back to its last point.
        const isProjected = !!projPoint && (!actualPoint || pointX(projPoint) > pointX(actualPoint));
        const x = (isProjected ? pointX(projPoint) : pointX(actualPoint)) ?? pointX(points[0]) ?? this.x;
        const label = categories[x] ?? x;
        const format = (v) =>
          Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
        const currentY = Number((isProjected ? projPoint?.y : actualPoint?.y) ?? 0);
        let tierIndex = Math.max(0, tierLabelOffset - 1);
        for (let i = 0; i < tiers.length; i++) {
          if (currentY >= tiers[i]) {
            tierIndex = i + tierLabelOffset;
          }
        }
        const lines = points
          .filter((p) => {
            const id = p.series?.options?.id;
            if (id === 'cumulative-sales') {
              return !isProjected;
            }
            if (id === 'projected-cumulative-sales') {
              return isProjected;
            }
            return true;
          })
          .map((p) => {
            const id = p.series?.options?.id;
            const name = id === 'projected-cumulative-sales' ? 'Estimated Sales' : p.series?.name;
            // The prior period line is plotted by day offset, so its own date has to be spelled out
            const priorDate = id === 'prior-cumulative-sales' ? priorCategories?.[x] : null;
            return `${name}${priorDate ? ` (${priorDate})` : ''}: ${format(p.y)}`;
          });
        return `<b>${label}</b><br/>${lines.join('<br/>')}<br/>Status: Tier ${tierIndex}`;
      },
    },
    plotOptions: {
      series: {
        animation: { duration: 300 },
        marker: { enabled: false, radius: 3, symbol: 'circle' },
        lineWidth: 3,
        turboThreshold: 0,
      },
    },
    series: [
      {
        id: 'cumulative-sales',
        type: 'spline',
        name: 'Cumulative Sales',
        data: points,
        zones,
        zoneAxis: 'y',
        zIndex: 2,
      },
      ...(hasProjection ?
        [
          {
            id: 'projected-cumulative-sales',
            type: 'line',
            name: 'Projected Sales',
            data: projPoints,
            zones,
            zoneAxis: 'y',
            dashStyle: 'Dot',
            lineWidth: 2,
            zIndex: 2,
          },
        ]
      : []),
      ...(hasPrior ?
        [
          {
            id: 'prior-cumulative-sales',
            type: 'spline',
            name: priorLabel,
            data: priorPoints,
            color: theme.palette.text.disabled,
            dashStyle: 'ShortDash',
            lineWidth: 2,
            zIndex: 1,
          },
        ]
      : []),
    ],
  };

  return <BaseHighchart options={options} />;
};
